import { Alert, Box, Button } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Panel } from "../components/layout/Panel";
import { createChannelForService } from "../api/client";
import { useHierarchyContext } from "../contexts/HierarchyContext";
import {
  AlertChannelForm,
  type AlertChannelFormState,
} from "../components/alerts/AlertChannelForm";

const initialFormState: AlertChannelFormState = {
  name: "",
  channelType: "webhook",
  enabled: true,
  webhookUrl: "",
  ntfyTopic: "",
  ntfyServer: "https://ntfy.sh",
  ntfyAuthType: "none",
  ntfyUser: "",
  ntfyPass: "",
  ntfyBearerToken: "",
  emailRecipients: "",
  telegramToken: "",
  telegramChatId: "",
};

export function PageAlertChannelNew() {
  const { hierarchy, smtpConfigured } = useHierarchyContext();
  const serviceSlug = hierarchy?.service?.slug ?? null;
  const navigate = useNavigate();
  const [formState, setFormState] = useState<AlertChannelFormState>(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buildConfig = (): Record<string, unknown> => {
    const { channelType, ntfyAuthType } = formState;
    switch (channelType) {
      case "webhook":
        return { url: formState.webhookUrl.trim() };
      case "ntfy":
        return {
          topic: formState.ntfyTopic.trim(),
          server: formState.ntfyServer.trim() || "https://ntfy.sh",
          auth_type: ntfyAuthType,
          ...(ntfyAuthType === "token" &&
            formState.ntfyBearerToken && { bearer_token: formState.ntfyBearerToken }),
          ...(ntfyAuthType === "password" &&
            formState.ntfyUser &&
            formState.ntfyPass && {
              username: formState.ntfyUser,
              password: formState.ntfyPass,
            }),
        };
      case "email":
        return {
          recipients: formState.emailRecipients
            .split(",")
            .map((e) => e.trim())
            .filter(Boolean),
        };
      case "telegram":
        return {
          bot_token: formState.telegramToken.trim(),
          chat_id: formState.telegramChatId.trim(),
        };
      default:
        return {};
    }
  };

  const validate = (): string | null => {
    const config = buildConfig();
    const { channelType, ntfyAuthType } = formState;

    if (channelType === "webhook" && !(config.url as string)) {
      return "URL is required";
    }
    if (channelType === "ntfy") {
      if (!(config.topic as string)) return "Topic is required";
      if (ntfyAuthType === "token" && !formState.ntfyBearerToken.trim()) {
        return "Bearer token is required when using token authentication";
      }
      if (ntfyAuthType === "password" && (!formState.ntfyUser.trim() || !formState.ntfyPass)) {
        return "Username and password are required when using password authentication";
      }
    }
    if (channelType === "email" && (config.recipients as string[]).length === 0) {
      return "At least one recipient is required";
    }
    if (
      channelType === "telegram" &&
      (!(config.bot_token as string) || !(config.chat_id as string))
    ) {
      return "Bot token and Chat ID are required";
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!serviceSlug) return;

    const err = validate();
    if (err) {
      setError(err);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await createChannelForService(
        serviceSlug,
        {
          name: formState.name.trim() || formState.channelType,
          channel_type: formState.channelType,
          channel_config: buildConfig(),
          enabled: formState.enabled,
        },
        undefined,
      );
      navigate(`/services/${encodeURIComponent(serviceSlug)}/alerts`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create channel");
    } finally {
      setSubmitting(false);
    }
  };

  if (!serviceSlug) {
    return (
      <Alert severity="warning">No Service in URL. Please navigate from the alerts page.</Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Panel title="Create notification channel">
        <AlertChannelForm
          mode="create"
          state={formState}
          onChange={(updates) => setFormState((prev) => ({ ...prev, ...updates }))}
          smtpConfigured={smtpConfigured}
          error={error}
          onErrorDismiss={() => setError(null)}
          submitting={submitting}
          onSubmit={handleSubmit}
          onCancel={() => navigate(-1)}
          submitLabel="Create"
        />
      </Panel>

      <Box sx={{ mt: 2 }}>
        <Button
          variant="text"
          onClick={() => navigate(`/services/${encodeURIComponent(serviceSlug)}/alerts`)}
        >
          Back to alerts
        </Button>
      </Box>
    </Box>
  );
}
